import React, { useCallback, useEffect, useRef, useState } from "react";
import { ImageOff } from "lucide-react";
import api from "config/axiosConfig";
import { apiRoutes } from "util/apiRoutes";
import { resolveMediaUrl } from "util/mediaUrl";
import { isSaneAspectRatio } from "util/mediaAspect";

/**
 * Source descriptor accepted by `CarouselImage`. Mirrors the subset of
 * `ExistingMediaFile` that the image renderer needs without dragging in the
 * full type so the component can also serve preview slots that only have an
 * object URL.
 */
export interface CarouselImageSource {
  /** Stable id for measurement reporting (typically media key or slot id). */
  id: string;
  /** Direct URL to render if no key/cdn resolution is needed. */
  src?: string;
  /** Media key for cdn resolution and signed-url refresh. */
  key?: string;
  /** Pre-computed CDN url (may be unsigned). */
  url?: string;
  /** Pre-signed URL if available. */
  signed_url?: string;
  /** MIME type. Used to enable SVG-specific fallback handling. */
  mimetype?: string;
  /** Image-CDN base for resolution. */
  imageCdn?: string;
  /** Optional alt text override. */
  alt?: string;
}

interface SignedMediaRefreshResponse {
  key?: string;
  url?: string;
  signed_url?: string;
  expires_at?: string;
}

const CF_SIGNATURE_PARAM_PATTERN = /[?&](Expires|Policy|Signature|Key-Pair-Id)=/i;

function hasSignedCloudFrontQuery(value: string): boolean {
  return CF_SIGNATURE_PARAM_PATTERN.test(String(value || ""));
}

function extractMediaKeyFromUrl(value: string): string {
  const rawValue = String(value || "").trim();
  if (!rawValue) return "";
  try {
    const parsed = new URL(rawValue);
    return parsed.pathname.replace(/^\/+/, "");
  } catch {
    return rawValue.replace(/^https?:\/\/[^/]+\/?/, "").replace(/^\/+/, "");
  }
}

interface CarouselImageProps {
  /** Source descriptor to render. */
  source: CarouselImageSource;
  /** Reported aspect ratio once the image decodes. */
  onMeasure?: (id: string, aspectRatio: number) => void;
  /** Loading attribute for the underlying `<img>`. Defaults to "lazy". */
  loading?: "lazy" | "eager";
  /** Inner image className; forwarded to both the `<img>` and `<object>` paths. */
  className?: string;
  /** Optional click handler; clicks bubble unless caller stops propagation. */
  onClick?: (e: React.MouseEvent<HTMLElement>) => void;
}

const isSvgSource = (source: CarouselImageSource): boolean => {
  if (
    typeof source.mimetype === "string" &&
    source.mimetype.toLowerCase() === "image/svg+xml"
  ) {
    return true;
  }
  const haystacks = [source.src, source.url, source.key, source.signed_url];
  return haystacks.some((value) =>
    typeof value === "string" && /\.svg(\?|#|$)/i.test(value),
  );
};

const buildInitialUrl = (source: CarouselImageSource): string => {
  if (source.src && /^(https?:|blob:|data:)/i.test(source.src)) {
    return source.src;
  }
  return resolveMediaUrl(
    {
      key: source.key ?? null,
      url: source.url ?? null,
      signed_url: source.signed_url ?? null,
    },
    source.imageCdn ?? "",
  );
};

/**
 * Image renderer for the post-card media carousel.
 *
 * Behavior summary:
 * - Lazy-loads with `decoding="async"` and `referrerPolicy="no-referrer"`.
 * - Reports intrinsic aspect ratio via `onMeasure` once the image decodes.
 * - On the first decode failure for a CloudFront-signed URL, transparently
 *   refreshes the signed URL via `/api/media/sign` and retries (mirrors
 *   `VideoPlayer.tryRefreshSignedUrl`).
 * - For SVG sources that still fail to decode as `<img>`, falls back to an
 *   `<object data type="image/svg+xml">` swap which uses a slightly different
 *   request profile and frequently rescues SVGs that hit `<img>` quirks.
 * - On terminal failure, renders an inline broken-image placeholder rather
 *   than the browser's default missing-image glyph.
 */
const CarouselImage: React.FC<CarouselImageProps> = ({
  source,
  onMeasure,
  loading = "lazy",
  className,
  onClick,
}) => {
  const isSvg = isSvgSource(source);

  const [resolvedUrl, setResolvedUrl] = useState<string>(() =>
    buildInitialUrl(source),
  );
  const [phase, setPhase] = useState<
    "image" | "object-fallback" | "broken"
  >("image");
  const refreshAttemptedRef = useRef<boolean>(false);
  const objectFallbackUsedRef = useRef<boolean>(false);

  // Reset state when the source identity changes so swapping items in the
  // parent does not preserve stale broken state.
  useEffect(() => {
    setResolvedUrl(buildInitialUrl(source));
    setPhase("image");
    refreshAttemptedRef.current = false;
    objectFallbackUsedRef.current = false;
  }, [source]);

  const tryRefreshSignedUrl = useCallback(async (): Promise<boolean> => {
    if (refreshAttemptedRef.current) return false;
    refreshAttemptedRef.current = true;

    const candidateKey =
      (source.key && source.key.trim()) ||
      extractMediaKeyFromUrl(resolvedUrl) ||
      "";
    const shouldRefresh = Boolean(
      candidateKey &&
        (hasSignedCloudFrontQuery(resolvedUrl) ||
          hasSignedCloudFrontQuery(source.signed_url || "")),
    );
    if (!shouldRefresh) return false;

    try {
      const response = await api.post<SignedMediaRefreshResponse>(
        apiRoutes.media.sign,
        { key: candidateKey },
      );
      const refreshedUrl = String(
        response.data?.signed_url || response.data?.url || "",
      ).trim();
      if (!refreshedUrl) return false;
      setResolvedUrl(refreshedUrl);
      setPhase("image");
      return true;
    } catch {
      return false;
    }
  }, [resolvedUrl, source.key, source.signed_url]);

  const handleLoad = useCallback(
    (event: React.SyntheticEvent<HTMLImageElement>) => {
      if (!onMeasure) return;
      const target = event.currentTarget;
      if (target.naturalWidth > 0 && target.naturalHeight > 0) {
        const ratio = parseFloat(
          (target.naturalWidth / target.naturalHeight).toFixed(4),
        );
        if (isSaneAspectRatio(ratio)) {
          onMeasure(source.id, ratio);
        }
      }
    },
    [onMeasure, source.id],
  );

  const handleError = useCallback(async () => {
    const refreshed = await tryRefreshSignedUrl();
    if (refreshed) return;
    if (isSvg && !objectFallbackUsedRef.current) {
      objectFallbackUsedRef.current = true;
      setPhase("object-fallback");
      return;
    }
    setPhase("broken");
  }, [isSvg, tryRefreshSignedUrl]);

  const altText = source.alt || "Media";
  const baseClass = className ?? "w-full h-full object-contain object-center";

  if (phase === "broken") {
    return (
      <div
        className="flex flex-col items-center justify-center gap-2 w-full h-full bg-eoe-bg-modal dark:bg-eoe-dark-bg-modal text-eoe-text-meta dark:text-eoe-dark-text-meta"
        role="img"
        aria-label={altText}
        onClick={onClick}
      >
        <ImageOff className="size-8" aria-hidden />
        <span className="text-xs">Could not load image</span>
      </div>
    );
  }

  if (phase === "object-fallback") {
    // `<object>` renders SVG via its own pipeline which often succeeds when
    // `<img>` fails (e.g. some inline-style CSP-edge cases). The pointer
    // events are forwarded to the click handler via the wrapper div.
    return (
      <div
        className="w-full h-full flex items-center justify-center"
        onClick={onClick}
      >
        <object
          data={resolvedUrl}
          type="image/svg+xml"
          aria-label={altText}
          className={baseClass}
          onError={() => setPhase("broken")}
        >
          <span className="sr-only">{altText}</span>
        </object>
      </div>
    );
  }

  return (
    <img
      src={resolvedUrl}
      alt={altText}
      className={baseClass}
      loading={loading}
      decoding="async"
      referrerPolicy="no-referrer"
      onLoad={handleLoad}
      onError={handleError}
      onClick={onClick}
    />
  );
};

export default CarouselImage;
